/**
 * PICK UP WHERE YOU LEFT OFF (2026-09-08).
 *
 * Katie McCarthy, ECH standup: the Airtable form the divisions use today "does store
 * your entry — if you start filling out a form and then you exit out of that form,
 * it saves that entry… so if you even got halfway through everything that you already
 * started." This app kept nothing: close the tab mid-build and the request, the
 * audience and the Studio document were gone. Jack: "it's something we can accomplish
 * now… the main thing is making sure, at what point do you save the HTML you're
 * working on — the fixation is more about building out the eblast and ensuring that
 * you can pick it back up."
 *
 * So the whole in-progress request is held in the browser — Step-1/2 fields, the
 * audience segment, the wizard page, and the Studio document as BLOCKS (not rendered
 * HTML, so every cap, lock and dynamic fill still applies when it comes back) — and
 * offered back on the next visit. Same storage the block library and the dup-jobs
 * cache already use, same rules:
 *
 *  1. NEVER AUTO-RESTORE. A draft is OFFERED, with the project name and how old it is;
 *     the requester chooses Resume or Start fresh. Silently reloading last week's
 *     half-request over a fresh visit is worse than losing it.
 *  2. AN EMPTY FORM NEVER OVERWRITES A DRAFT. Saving is gated on `draftMeaningful` and
 *     on the offer having been answered, so the blank initial state at mount cannot
 *     clobber what is stored (the failure this file's first draft had).
 *  3. A SUCCESSFUL SUBMIT CLEARS IT. So does "Submit another" and "Start fresh". A
 *     failed submit keeps it — that is exactly when someone needs to come back.
 *  4. PER SIGNED-IN USER. Keyed by the requester's email so a shared machine does not
 *     hand one division another's request. Unknown user → one anonymous slot.
 *  5. QUOTA IS NOT AN ERROR. localStorage is ~5MB; a document carrying huge inline
 *     data is skipped (nothing stored, nothing thrown) rather than half-written.
 */
import type { FormState } from "./submit";
import type { SegState } from "./segData";
import type { AnyBlockInstance } from "../email/document";

export const DRAFT_KEY_PREFIX = "esDraft.v1:";
/** how long after the last change the draft is written */
export const DRAFT_DEBOUNCE_MS = 600;
/** a draft older than this is not offered — a two-week-old half request is noise */
export const DRAFT_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;
/** above this the draft is not stored (localStorage quota is ~5MB per origin) */
const DRAFT_MAX_BYTES = 4_000_000;

export interface Draft {
  v: 1;
  savedAt: number;
  who: string;
  form: FormState;
  seg: SegState;
  formStep: number;
  blocks: AnyBlockInstance[];
  tplHtml: string;
  tplEdited: string;
  tplBespoke: boolean;
  dupLoaded: boolean;
}

export function draftKey(who: string | undefined | null): string {
  const w = String(who || "").trim().toLowerCase();
  return DRAFT_KEY_PREFIX + (w || "anon");
}

/**
 * Is there anything here worth keeping? Only signals the REQUESTER produced count.
 * The form seeds defaults at mount — a template ("Coming Soon"), a subject line, a
 * request type, a focus — so those can never mean "started": measured, a blank mount
 * read as meaningful through them and overwrote the stored draft with an empty
 * form, which is the one thing this file must never do. A project name, a picked
 * community/masterplan, a step beyond the first, a template edit, or a Studio
 * document that diverged from the starter mock is the requester's own work.
 */
export function draftMeaningful(form: FormState, docBuilt: boolean, tplEdited: string, formStep = 1): boolean {
  const f = form as unknown as Record<string, unknown>;
  const has = (k: string) => {
    const v = f[k];
    if (Array.isArray(v)) return v.length > 0;
    return String(v ?? "").trim() !== "";
  };
  return docBuilt || !!tplEdited || formStep > 1 || has("projectName") || has("community") || has("masterplan");
}

export function draftSave(d: Omit<Draft, "v" | "savedAt">): boolean {
  try {
    const rec: Draft = { v: 1, savedAt: Date.now(), ...d };
    const s = JSON.stringify(rec);
    if (s.length > DRAFT_MAX_BYTES) return false;
    localStorage.setItem(draftKey(d.who), s);
    return true;
  } catch {
    return false; /* private mode / quota — the request is still on screen */
  }
}

export function draftLoad(who: string | undefined | null, now: number = Date.now()): Draft | null {
  try {
    const raw = localStorage.getItem(draftKey(who));
    if (!raw) return null;
    const d = JSON.parse(raw) as Draft;
    if (!d || d.v !== 1 || !d.form || !Array.isArray(d.blocks)) return null;
    if (!(typeof d.savedAt === "number") || now - d.savedAt > DRAFT_MAX_AGE_MS) return null;
    return d;
  } catch {
    return null;
  }
}

export function draftClear(who: string | undefined | null): void {
  try { localStorage.removeItem(draftKey(who)); } catch { /* nothing to clear */ }
}

/** "just now" · "12 min ago" · "3 hours ago" · "yesterday" · "4 days ago" */
export function draftAge(savedAt: number, now: number = Date.now()): string {
  const s = Math.max(0, Math.round((now - savedAt) / 1000));
  if (s < 60) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return m + " min ago";
  const h = Math.round(m / 60);
  if (h < 24) return h + (h === 1 ? " hour ago" : " hours ago");
  const d = Math.round(h / 24);
  return d === 1 ? "yesterday" : d + " days ago";
}

/** what the offer calls the draft: project name, else the template, else a stand-in */
export function draftTitle(d: Draft): string {
  const f = d.form as unknown as Record<string, unknown>;
  const name = String(f.projectName || "").trim();
  if (name) return name;
  const t = f.template;
  const tpl = Array.isArray(t) ? String(t[0] || "") : String(t || "");
  if (tpl.trim()) return tpl.trim() + " (unnamed)";
  return "Unnamed request";
}
